// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from "react";
import {
  onAdminAuthState, getAdminProfile, createAdminRecord,
  signInWithEmail, signInWithGoogle, signOutAdmin,
  getPermissions, logActivity,
} from "../firebase/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // Firebase Auth user
  const [profile, setProfile] = useState(null);   // Firestore admin record
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const ensureGoogleAdminProfile = async (firebaseUser) => {
    let adminProfile = await getAdminProfile(firebaseUser.uid);
    const isGoogleUser = firebaseUser.providerData?.some(
      (provider) => provider?.providerId === "google.com"
    );

    if (!adminProfile && isGoogleUser) {
      await createAdminRecord(
        firebaseUser.uid,
        {
          name: firebaseUser.displayName || "New User",
          email: firebaseUser.email,
          role: "viewer",
        },
        firebaseUser.uid
      );
      adminProfile = await getAdminProfile(firebaseUser.uid);
    }

    return adminProfile;
  };

  useEffect(() => {
    const unsub = onAdminAuthState(async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null); setProfile(null); setPermissions(null);
        setLoading(false);
        return;
      }

      const adminProfile = await ensureGoogleAdminProfile(firebaseUser);
      if (adminProfile && adminProfile.active) {
        setUser(firebaseUser);
        setProfile(adminProfile);
        setPermissions(getPermissions(adminProfile.role));
      } else {
        // Signed in to Firebase Auth but no admin record → not authorised
        await signOutAdmin();
        setUser(null); setProfile(null); setPermissions(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmail(email, password);
    const adminProfile = await getAdminProfile(result.user.uid);
    if (!adminProfile || !adminProfile.active) {
      await signOutAdmin();
      throw new Error("You don't have admin access. Contact the Super Admin.");
    }
    await logActivity(result.user.uid, "login", `Logged in via email`);
    return adminProfile;
  };

  const loginWithGoogle = async () => {
    const result = await signInWithGoogle();
    if (result?.redirected) return { redirected: true };

    const adminProfile = await ensureGoogleAdminProfile(result.user);
    if (!adminProfile || !adminProfile.active) {
      await signOutAdmin();
      throw new Error("Your Google account doesn't have admin access. Contact the Super Admin.");
    }
    await logActivity(result.user.uid, "login", `Logged in via Google`);
    return adminProfile;
  };

  const logout = async () => {
    if (profile) await logActivity(profile.uid, "logout", "Logged out");
    await signOutAdmin();
  };

  // Convenience getters
  const admin = !!user && !!profile;
  const isSuperAdmin = profile?.role === "super_admin";

  return (
    <AuthContext.Provider value={{
      user, profile, permissions, loading,
      admin, isSuperAdmin,
      loginWithEmail, loginWithGoogle, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
